import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateRegisterPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('invite');
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    how_promote: ''
  });

  useEffect(() => {
    if (!inviteToken) {
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/affiliates/invite/${inviteToken}`)
      .then(res => { setValidToken(true); })
      .catch(() => { setValidToken(false); })
      .finally(() => setLoading(false));
  }, [inviteToken]);

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/register`, {
        ...form,
        invite_token: inviteToken
      });
      setAffiliateCode(res.data.affiliate_code);
      setRegistered(true);
      toast.success('Welcome to the Sister Diamonds Affiliate Program!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-opacity-40";
  const labelCls = "block text-sm font-medium mb-1.5";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F8F6' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#C06C54' }} />
      </div>
    );
  }

  if (!inviteToken || !validToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-bold" style={{ color: '#1A3C34', fontFamily: "'Playfair Display', serif" }}>
            Invalid or Expired Invitation
          </h1>
          <p className="text-sm" style={{ color: 'rgba(26,60,52,0.6)', fontFamily: "'Manrope', sans-serif" }}>
            This affiliate invitation link is no longer valid. Please contact us for a new invitation.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-full text-sm font-medium transition-all"
            style={{ backgroundColor: '#1A3C34', color: '#E8DCCA' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-lg w-full text-center space-y-6 p-8 rounded-2xl" style={{ backgroundColor: 'white', boxShadow: '0 4px 24px rgba(26,60,52,0.08)' }}>
          <CheckCircle className="h-16 w-16 mx-auto" style={{ color: '#1A3C34' }} />
          <h1 className="text-2xl font-bold" style={{ color: '#1A3C34', fontFamily: "'Playfair Display', serif" }}>
            Welcome to the Affiliate Program!
          </h1>
          <p className="text-sm" style={{ color: 'rgba(26,60,52,0.6)', fontFamily: "'Manrope', sans-serif" }}>
            Your registration is pending review. Once approved, you'll receive an email with your dashboard access and referral links.
          </p>
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(192,108,84,0.06)', border: '1px solid rgba(192,108,84,0.15)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: '#C06C54' }}>Your Affiliate Code</p>
            <p className="text-lg font-mono font-bold" style={{ color: '#1A3C34' }} data-testid="affiliate-code">{affiliateCode}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-full text-sm font-medium transition-all"
            style={{ backgroundColor: '#1A3C34', color: '#E8DCCA' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F8F6' }}>
      {/* Hero */}
      <div className="py-16 px-4 text-center" style={{ backgroundColor: '#1A3C34' }}>
        <p className="text-xs tracking-[0.25em] uppercase mb-4" style={{ color: '#C06C54', fontFamily: "'Manrope', sans-serif" }}>
          Sister Diamonds Affiliate Program
        </p>
        <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#E8DCCA', fontFamily: "'Playfair Display', serif" }}>
          Partner With Us
        </h1>
        <p className="text-sm max-w-lg mx-auto" style={{ color: 'rgba(232,220,202,0.7)', fontFamily: "'Manrope', sans-serif" }}>
          Join the Sister Diamonds affiliate network and earn commissions by sharing transformative experiences with your audience.
        </p>
      </div>

      {/* Form */}
      <div className="max-w-xl mx-auto px-4 py-12">
        <form onSubmit={handleSubmit} className="space-y-5 p-6 md:p-8 rounded-2xl" style={{ backgroundColor: 'white', boxShadow: '0 4px 24px rgba(26,60,52,0.08)' }}>
          <h2 className="text-xl font-bold mb-4" style={{ color: '#1A3C34', fontFamily: "'Playfair Display', serif" }}>
            Affiliate Registration
          </h2>

          <div>
            <label className={labelCls} style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Full Name *</label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => update('full_name', e.target.value)}
              className={inputCls}
              style={{ borderColor: 'rgba(26,60,52,0.15)', fontFamily: "'Manrope', sans-serif" }}
              placeholder="Your full name"
              data-testid="affiliate-name-input"
              required
            />
          </div>

          <div>
            <label className={labelCls} style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Email Address *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => update('email', e.target.value)}
              className={inputCls}
              style={{ borderColor: 'rgba(26,60,52,0.15)', fontFamily: "'Manrope', sans-serif" }}
              placeholder="your@email.com"
              data-testid="affiliate-email-input"
              required
            />
          </div>

          <div>
            <label className={labelCls} style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Phone Number</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => update('phone', e.target.value)}
              className={inputCls}
              style={{ borderColor: 'rgba(26,60,52,0.15)', fontFamily: "'Manrope', sans-serif" }}
              placeholder="(555) 000-0000"
              data-testid="affiliate-phone-input"
            />
          </div>

          <div>
            <label className={labelCls} style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Company / Brand</label>
            <input
              type="text"
              value={form.company}
              onChange={e => update('company', e.target.value)}
              className={inputCls}
              style={{ borderColor: 'rgba(26,60,52,0.15)', fontFamily: "'Manrope', sans-serif" }}
              placeholder="Your company or brand name"
              data-testid="affiliate-company-input"
            />
          </div>

          <div>
            <label className={labelCls} style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>Website / Social Media</label>
            <input
              type="text"
              value={form.website}
              onChange={e => update('website', e.target.value)}
              className={inputCls}
              style={{ borderColor: 'rgba(26,60,52,0.15)', fontFamily: "'Manrope', sans-serif" }}
              placeholder="https://yoursite.com or @handle"
              data-testid="affiliate-website-input"
            />
          </div>

          <div>
            <label className={labelCls} style={{ color: '#1A3C34', fontFamily: "'Manrope', sans-serif" }}>How do you plan to promote Sister Diamonds?</label>
            <textarea
              value={form.how_promote}
              onChange={e => update('how_promote', e.target.value)}
              className={inputCls}
              style={{ borderColor: 'rgba(26,60,52,0.15)', fontFamily: "'Manrope', sans-serif", minHeight: '100px' }}
              placeholder="Tell us about your audience and promotion strategy..."
              data-testid="affiliate-promote-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: '#C06C54', color: 'white', fontFamily: "'Manrope', sans-serif" }}
            data-testid="affiliate-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Apply to Join <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AffiliateRegisterPage;
